import { Drawer } from './ui/Drawer';
import { Badge } from './ui/primitives';
import { ConfidenceBand } from './ConfidenceBand';
import { CoordinateMarks, LedgerRow, SplitPane } from './identity';
import { formatRelative } from '@/lib/utils';
import type { Evidence } from '@/types';

/* ----------------------------------------------------------------------------
 * The Evidence Drawer (signature surface, v1.1 §6.4) — *why* any number exists.
 * Carries the identity system: elevation-2 sheet, a locked 63:37 internal split
 * (factors vs. metadata rail), corner coordinate marks, and a rule-separated
 * factor ledger with a mono index column. Reusable across every engine.
 * -------------------------------------------------------------------------- */
export function EvidenceDrawer({
  open,
  onClose,
  label,
  score,
  evidence,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  score: number | string;
  evidence: Evidence;
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      accent
      title={`Why: ${label}`}
      widthClass="w-[580px] max-w-[94vw]"
    >
      <div className="relative">
        <CoordinateMarks />
        <SplitPane
          className="gap-0"
          primary={
            <div className="pr-5">
              {/* Headline number + advisory rationale */}
              <div className="font-data text-[2.5rem] font-semibold leading-none text-text-display" data-tabular>
                {score}
              </div>
              <div className="mt-2 text-caption text-text-tertiary">
                {evidence.method}
              </div>

              {evidence.ai_rationale && (
                <div className="mt-4 rounded-md border-l-2 border-l-ai bg-ai-wash/50 px-3 py-2">
                  <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
                    <span aria-hidden>✧</span> Proposed by copilot
                  </div>
                  <p className="text-body-md text-text-primary">{evidence.ai_rationale}</p>
                </div>
              )}

              {/* Weighted factor ledger — rule-separated rows, mono index (§2.7.2) */}
              <div className="mt-6">
                <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
                  Contributing factors
                </div>
                {evidence.factors.map((f, i) => (
                  <LedgerRow key={f.label} index={i + 1} last={i === evidence.factors.length - 1}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-body-md font-medium text-text-primary">{f.label}</span>
                      <span className="font-data text-caption text-text-secondary" data-tabular>
                        {Math.round(f.weight * 100)}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-inset">
                      <div className="h-full rounded-full bg-engine" style={{ width: `${f.weight * 100}%` }} />
                    </div>
                    <div className="mt-1.5 text-caption text-text-tertiary">{f.detail}</div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <Badge tone="neutral">via {f.source}</Badge>
                      <span className="font-data text-[11px] text-text-tertiary" data-tabular>
                        {formatRelative(f.observed_at)}
                      </span>
                    </div>
                  </LedgerRow>
                ))}
              </div>
            </div>
          }
          secondary={
            /* metadata rail — leading tick edge, confidence + version stepper */
            <div className="relative h-full pl-5">
              <div className="tick-rail absolute left-0 top-0 h-full" aria-hidden />
              <div className="space-y-4">
                <div>
                  <div className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
                    Confidence
                  </div>
                  <ConfidenceBand level={evidence.confidence} />
                </div>
                <div>
                  <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
                    Version
                  </div>
                  <div className="font-data text-body-md text-text-primary" data-tabular>
                    {evidence.version}
                  </div>
                </div>
                <div>
                  <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
                    Updated
                  </div>
                  <div className="text-caption text-text-secondary">
                    {formatRelative(evidence.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          }
        />

        <p className="mt-6 border-t border-border-subtle pt-3 text-[11px] text-text-tertiary">
          Every factor is source-attributed. AI outputs are advisory and operator-editable —
          overrides are audit-logged with actor and prior value.
        </p>
      </div>
    </Drawer>
  );
}
